import { type MetaFunction } from '@remix-run/node'

export const meta: MetaFunction = () => [
	{ title: 'How does insurance work?' },
	{
		name: 'description',
		content: 'How Insurance Works',
	},
]

export default function Component() {
	return (
		<>
			<h1 className="py-3 text-4xl font-black">
				How does insurance work, anyway?
			</h1>
			<p
				data-paragraph
				className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
			>
				Insurance exists to fill a need in society. When you buy insurance,
				you're buying protection from the high cost of financial loss. Insurance
				companies fill this societal need by gathering data about the type of
				claims paid, the amount of payment, and other information, and then
				coupling it with very sophisticated statistical models, probabilities,
				and predictive analytics to determine the expected cost of paying all
				claims for a particular year. With this cost information, insurance
				companies are able to offer insurance policies for purchase. An
				insurance policy is a contract between a policy owner and an insurer.
				When the policy owner upholds his or her end of the contract by paying
				the premium and abiding by the conditions set forth in the policy, the
				insurer is obligated to uphold its duties contained in the policy.
			</p>
			<p
				data-paragraph
				className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
			>
				Calculating insurance costs is not a straightforward science. Insurace
				companies must consider all the data before estimating the cost of
				insurance. Because of the math involved, the larger the pool of data,
				the greater the insurance company's ability to accurately predict the
				true cost of claims. This is because of a mathematical principle called
				the law of large numbers, which states: as a sample size grows, the mean
				will grow closer and closer to the population average. This means that
				the actual performance of the group of policyholders will be closer to
				average and the insurer will have the ability to more accurately predict
				the cost of claims. Since there is so much uncertainty involved,
				sometimes the insurer makes money and sometimes they lose money.
			</p>
			<p
				data-paragraph
				className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
			>
				Underwriting is the name for the process that insurance companies use to
				gather information and screen potential policyholders. You may be
				wondering why they ask so many questions.
			</p>
			<p
				data-paragraph
				className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
			>
				Policyholders who buy this protection aren't guaranteeing that bad
				things won't happen, but they are avoiding some of the burdens of
				financial catastrophe by paying the insurance company to absorb the
				risks. The cost of an insurance policy is called the premium. A premium
				is the amount of money that is charged by the insurer for setting up and
				managing the insurance program. Insurance companies use the information
				about the cost and cause of claims to determine the premium amount.
				Sometimes the insurer earns a profit because the amount that they had to
				pay out in claims and expenses was less than the amount collected. The
				insurer exists to earn a profit so that it can build reserve funds and
				increase its financial stability.
			</p>
			<p
				data-paragraph
				className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
			>
				Many forms of insurance do not shift the entire financial burden over to
				the insurance company. Instead, the policy owner continues to bear some
				burden as an incentive to the owner to avoid claims and losses. This is
				called a deductible or a retention. For example, you might buy insurance
				for a car, and pay the insurance company $300 for one year of coverage
				against a collision accident. You would select a deductible offered on
				the policy when establishing coverage - for example, $500. In general,
				policies with lower premiums will have higher deductibles. After you pay
				the premium and the coverage is in force, if you have no accidents, you
				and the insurance company both “win.” You avoided the possibility of
				having to pay for a costly repair out of pocket, and the insurance
				company was able to use the $300 premium to pay claims for others who
				were less fortunate than you because they were involved in an accident.
				If you do have an accident, you are responsible for paying the first
				$500 (your deductible) before the insurance company pays out. The
				insurance company has the ability to pay its portion because of the
				premiums it has collected from you and others. With a higher deductible,
				you have more “skin” in the game and are more apt to be careful to avoid
				any type of claim that you are able to prevent.
			</p>
			<p
				data-paragraph
				className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
			>
				Use judgment when selecting a deductible. If you have a claim, it's
				really nice to not have to come up with a large amount of money in order
				to get the claim settled and the damage repaired. Over the course of a
				lifetime, however, my experience has shown that people prefer the
				savings that come from having a lower premium year after year.
				Ultimately, the decision of deductibles is one that you may need to
				debate after careful consideration of the available options.
			</p>
			<p
				data-paragraph
				className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
			>
				While the ability to shift the risk of loss to an insurer is very
				important in some situations, in others it is more prudent for a person
				to absorb the risk of loss on their own. A good example of this is if
				you drive an older car whose value has decreased significantly from when
				the vehicle was brand new. For most people, it makes more sense to NOT
				purchase collision insurance because after paying the premium and
				deductible, the amount of recovery after a claim will not be enough for
				the insurance to be a wise investment. A good rule of thumb is to ask
				yourself, if (insert type of claim here) happens, do I want the
				insurance company to respond and help me out with the cost of the claim,
				or would I prefer to take my chances?
			</p>
			<p
				data-paragraph
				className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
			>
				Similarly, even if you do have insurance coverage for a claim and could
				pursue payout from the insurance company, if the nature of the claim is
				small you may exercise the option of paying for the damage out of pocket
				instead of placing a small claim. Since the primary role of insurance is
				to avoid catastrophic financial burdens, it would be wise to avoid
				jeopardizing insurance coverage. Some insurers will raise rates or
				discontinue coverage if the frequency of claims is too high.
			</p>
		</>
	)
}
